<template>
  <div style="overflow-x: auto; overflow-y: hidden; padding: 14px; margin: 6px 0">
    <div v-if="items && items.length > 0">
      <chart-sorter v-model="sorting" :sorting-items="sortingItems" />

      <div class="chart-container" :style="{ width: width }">
        <bar-chart ref="barChart" :chart-data="chartData" :options="chartOptions" />
      </div>
    </div>
    <chart-empty-state v-else />
  </div>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep'
import { localeString } from '@/config/helpers.js'
import BarChart from '@/components/BarChart.vue'
import { splitUnitName, filterUnit, removeCommonElement } from '@/modules/industria/helpers/chartHelper.js'
import ChartSorter from './ChartSorter.vue'
import ChartEmptyState from '@/components/campaigns/ChartEmptyState.vue'

export default {
  name: 'BenefitClientsChart',
  components: {
    BarChart,
    ChartSorter,
    ChartEmptyState,
  },
  props: {
    selectedUnits: {
      type: Array,
      required: true,
    },
    items: {
      type: Array,
      default(_) {
        return []
      },
    },
    offerType: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    sorting: 'gb-desc',
    chartOptions: {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        yAxes: [
          {
            ticks: {
              beginAtZero: true,
              callback: (value, index, values) => {
                return localeString(value)
              },
            },
          },
        ],
        xAxes: [
          {
            gridLines: {
              display: false,
            },
          },
        ],
      },
      legend: {
        display: false,
      },
      tooltips: {
        callbacks: {
          title: (tooltipItem, data) => {
            return tooltipItem[0].xLabel.join(' ')
          },
          label: (tooltipItem, data) => {
            return `${data.datasets[tooltipItem.datasetIndex].label}: ${localeString(tooltipItem.yLabel)}`
          },
        },
      },
      layout: {
        padding: {
          top: 30,
        },
      },
      plugins: {
        datalabels: {
          clamp: true,
          anchor: 'end',
          align: 'top',
          offset: 0,
          formatter: (x) => localeString(x),
        },
      },
    },
  }),
  computed: {
    chartData() {
      let itemsClone = cloneDeep(this.items)
      const units = []
      const earnClients = []
      const burnClients = []

      switch (this.sorting) {
        case 'gb-asc':
          itemsClone.sort((a, b) => a.earn_clients - b.earn_clients)
          break
        case 'gb-desc':
          itemsClone.sort((a, b) => b.earn_clients - a.earn_clients)
          break
        case 'ub-asc':
          itemsClone.sort((a, b) => a.burn_clients - b.burn_clients)
          break
        case 'ub-desc':
          itemsClone.sort((a, b) => b.burn_clients - a.burn_clients)
          break
        case 'none':
        default:
          itemsClone = cloneDeep(this.items)
      }

      itemsClone = filterUnit(itemsClone, this.selectedUnits)
      itemsClone.forEach((item) => {
        units.push(splitUnitName(item.unit_name))
        earnClients.push(item.earn_clients)
        burnClients.push(item.burn_clients)
      })

      let datasets = [
        {
          label: `Ganharam ${this.offerTypeName}`,
          data: earnClients,
          backgroundColor: '#002040',
        },
        {
          label: `Usaram ${this.offerTypeName}`,
          data: burnClients,
          backgroundColor: '#00B395',
        },
      ]

      removeCommonElement(units)
      return {
        labels: units,
        datasets,
      }
    },

    sortingItems() {
      const sortingItems = [
        {
          sortBy: 'gb',
          name: `Ganharam ${this.offerTypeName}`,
          legendColor: '#002040',
        },
        {
          sortBy: 'ub',
          name: `Utilizaram ${this.offerTypeName}`,
          legendColor: '#00b395',
        },
      ]
      return sortingItems
    },

    width() {
      return this.chartData.datasets[0].data.length * 100 + 'px'
    },

    isNextbuyCashback() {
      return this.offerType === 'nextbuycashback'
    },

    isDigitalVoucher() {
      return this.offerType === 'dynamic_rule'
    },

    offerTypeName() {
      return this.isNextbuyCashback ? 'cashback' : this.isDigitalVoucher ? 'voucher' : ''
    },

    offerTypeNameCapital() {
      return this.isNextbuyCashback ? 'Cashback' : this.isDigitalVoucher ? 'Voucher' : ''
    },
  },
}
</script>

<style lang="scss" scoped>
.chart-container {
  position: relative;
  min-width: 100%;
}
</style>
